import { useState } from "react";
import { SlidersHorizontal } from "lucide-react";

export type AdvancedField = {
  key: string;
  label: string;
  type: "select" | "date" | "text";
  options?: { value: string; label: string }[];
  placeholder?: string;
};

type Props = {
  fields: AdvancedField[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onReset: () => void;
  /** Open on first render (e.g. when a saved view already uses advanced filters). */
  defaultOpen?: boolean;
};

/**
 * "More filters" for sections with many filters. Basic filters stay visible as chips;
 * everything else sits in this collapsible panel, with a count of the ones in use.
 */
export function AdvancedFilters({ fields, values, onChange, onReset, defaultOpen }: Props) {
  const active = fields.filter((f) => (values[f.key] ?? "") !== "").length;
  const [open, setOpen] = useState(Boolean(defaultOpen) || active > 0);
  return (
    <div className="space-y-2">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex min-h-9 items-center gap-1.5 rounded-full border border-border bg-card px-3 text-sm font-semibold text-muted-foreground transition hover:text-foreground focus-visible:ring-2 ring-ring"
      >
        <SlidersHorizontal size={14} aria-hidden="true" />
        More filters
        {active > 0 && <span className="rounded-full bg-primary px-1.5 text-xs font-bold text-on-accent">{active}</span>}
      </button>
      {open && (
        <div className="space-y-3 rounded-tile border border-border bg-card p-3 sm:p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {fields.map((f) => (
              <div key={f.key}>
                <label htmlFor={`adv-${f.key}`} className="mb-1 block text-xs font-semibold text-muted-foreground">
                  {f.label}
                </label>
                {f.type === "select" ? (
                  <select
                    id={`adv-${f.key}`}
                    value={values[f.key] ?? ""}
                    onChange={(e) => onChange(f.key, e.target.value)}
                    className="field w-full rounded-control px-3 py-2.5 text-base sm:text-sm"
                  >
                    <option value="">Any</option>
                    {f.options?.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id={`adv-${f.key}`}
                    type={f.type === "date" ? "date" : "text"}
                    value={values[f.key] ?? ""}
                    placeholder={f.placeholder}
                    onChange={(e) => onChange(f.key, e.target.value)}
                    className="field w-full rounded-control px-3 py-2.5 text-base sm:text-sm"
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <button type="button" onClick={onReset} disabled={active === 0} className="min-h-9 px-3 text-sm font-semibold text-muted-foreground hover:text-foreground disabled:opacity-40">
              Reset filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
